import AddIcon from '@mui/icons-material/Add'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Fragment, useState } from 'react'
import { describeError } from '../api/client'
import { useMySites } from '../api/hooks'
import {
  useDeleteTemplate,
  useTemplates,
  type EntryTemplate,
  type TemplateLine,
  type TemplateModule,
} from '../api/templates'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ActiveChip } from '../components/StatusChip'
import { TemplateEditorDialog } from './templates/TemplateEditorDialog'

const MODULES: { module: TemplateModule; label: string; blurb: string }[] = [
  {
    module: 'WORK',
    label: 'Work completion',
    blurb: 'The BOQ lines a normal day works on. Applying fills in the planned quantity and leaves completed at zero.',
  },
  {
    module: 'LABOUR',
    label: 'Labour worked',
    blurb: 'The gang expected on site. Applying fills in the planned headcount; who actually turned up is entered on the day.',
  },
  {
    module: 'MATERIAL',
    label: 'Material received',
    blurb: 'The deliveries a site regularly orders. Applying fills in the ordered quantity and leaves received at zero until the lorry arrives.',
  },
]

/**
 * Every saved template in one place.
 *
 * <p>Templates are made from the entry screens - enter a normal day, keep it - so this
 * screen is for what comes afterwards: seeing what a site has accumulated, correcting the
 * numbers when the standard gang changes, and retiring the ones nobody uses.</p>
 */
export function TemplatesPage() {
  const sites = useMySites()
  const [siteId, setSiteId] = useState('')
  const [tab, setTab] = useState(0)

  const chosenSite = siteId || sites.data?.[0]?.id || ''
  const module = MODULES[tab]

  return (
    <>
      <PageHeader
        title="Entry templates"
        subtitle="A site's repeated day, saved once and applied from the daily entry screens."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Site"
          value={chosenSite}
          onChange={(e) => setSiteId(e.target.value)}
          sx={{ minWidth: 280 }}
          helperText="Templates belong to a site, not to the company"
        >
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.code} - {s.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {sites.isLoading && <LoadingBlock />}
      {(sites.data?.length ?? 0) === 0 && !sites.isLoading && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            You have no sites assigned, so there is nothing to template yet.
          </Typography>
        </Paper>
      )}

      {chosenSite && (
        <Paper variant="outlined">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {MODULES.map((m) => (
              <Tab key={m.module} label={m.label} />
            ))}
          </Tabs>
          {module && <ModulePanel siteId={chosenSite} module={module.module} blurb={module.blurb} />}
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Build one here, or capture a real day with "Save today as template" on the entry
        screens. Applying a template creates ordinary entries; nothing stays linked to it
        afterwards, so editing a template never changes a day already recorded.
      </Typography>
    </>
  )
}

function ModulePanel({
  siteId,
  module,
  blurb,
}: {
  siteId: string
  module: TemplateModule
  blurb: string
}) {
  const templates = useTemplates(module, { siteId })
  const remove = useDeleteTemplate()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editing, setEditing] = useState<EntryTemplate | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirming, setConfirming] = useState<EntryTemplate | null>(null)
  const [error, setError] = useState<unknown>(null)

  const rows = templates.data ?? []

  if (templates.isLoading) return <LoadingBlock />
  if (templates.error) {
    return (
      <Box sx={{ p: 2 }}>
        <ErrorBlock error={templates.error} />
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 280 }}>
          {blurb}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New template
        </Button>
      </Box>

      {Boolean(error) && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {describeError(error)}
          </Alert>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Template</TableCell>
              <TableCell>Contents</TableCell>
              <TableCell align="right">Lines</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((template) => (
              // A fragment so the expanded lines are a sibling row rather than nested markup.
              <Fragment key={template.id}>
                <TableRow hover>
                  <TableCell sx={{ width: 40 }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpanded(expanded === template.id ? null : template.id)}
                      sx={{
                        transform: expanded === template.id ? 'rotate(180deg)' : 'none',
                        transition: 'transform 150ms',
                      }}
                    >
                      <ExpandMore fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>{template.summary}</TableCell>
                  <TableCell align="right">{template.lineCount}</TableCell>
                  <TableCell align="right">{template.useCount}</TableCell>
                  <TableCell>{formatWhen(template.lastUsedAt)}</TableCell>
                  <TableCell>
                    <ActiveChip active={template.active} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing(template)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirming(template)}>
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                    <Collapse in={expanded === template.id} unmountOnExit>
                      <Box sx={{ py: 2, pl: 6, pr: 2 }}>
                        <LineList module={module} lines={template.lines} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No templates for this site yet. Enter a normal day on the entry screen and
                  use "Save today as template".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(creating || editing) && (
        <TemplateEditorDialog
          siteId={siteId}
          module={module}
          template={editing ?? undefined}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete "{confirming?.name}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The template goes; days already entered from it are untouched, because nothing
            stays linked once it has been applied.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={async () => {
              setError(null)
              try {
                if (confirming) await remove.mutateAsync(confirming.id)
                setConfirming(null)
              } catch (err) {
                setError(err)
                setConfirming(null)
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/** What a template actually contains, in the words of its own module. */
function LineList({ module, lines }: { module: TemplateModule; lines: TemplateLine[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {module === 'WORK' && (
            <>
              <TableCell>BOQ</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Planned quantity</TableCell>
            </>
          )}
          {module === 'LABOUR' && (
            <>
              <TableCell>Labours</TableCell>
              <TableCell>Whose labour</TableCell>
              <TableCell align="right">Planned workers</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">OT</TableCell>
            </>
          )}
          {module === 'MATERIAL' && (
            <>
              <TableCell>Material</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Whose material</TableCell>
              <TableCell align="right">Ordered quantity</TableCell>
              <TableCell align="right">Rate</TableCell>
            </>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.id}>
            {module === 'WORK' && (
              <>
                <TableCell sx={{ fontFamily: 'monospace' }}>{line.workItemCode ?? '-'}</TableCell>
                <TableCell>{line.workItemDescription ?? '-'}</TableCell>
                <TableCell align="right">
                  {line.defaultQuantity ?? '-'} {line.uomCode ?? ''}
                </TableCell>
              </>
            )}
            {module === 'LABOUR' && (
              <>
                <TableCell>{line.labourCategoryName ?? '-'}</TableCell>
                <TableCell>{line.contractorName ?? 'Own workforce'}</TableCell>
                <TableCell align="right">{line.workerCount ?? '-'}</TableCell>
                <TableCell align="right">{line.workingHours ?? '-'}</TableCell>
                <TableCell align="right">{line.overtimeHours ?? '-'}</TableCell>
              </>
            )}
            {module === 'MATERIAL' && (
              <>
                <TableCell>
                  {`${line.materialCode ?? ''} ${line.materialName ?? ''}`.trim() || '-'}
                </TableCell>
                <TableCell>{line.supplierName ?? '-'}</TableCell>
                <TableCell>
                  {line.supplyScope === 'CONTRACTOR'
                    ? `Contractor${line.contractorName ? `: ${line.contractorName}` : ''}`
                    : line.supplyScope === 'CLIENT'
                      ? 'Client issued'
                      : 'Own'}
                </TableCell>
                <TableCell align="right">{line.defaultQuantity ?? '-'}</TableCell>
                <TableCell align="right">{line.rate ?? '-'}</TableCell>
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
function formatWhen(value?: string): string {
  if (!value) return 'never'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
